// vorgangPickerListe.ts — reine Darstellungs-Logik der gestuften Vorgangs-
// Auswahl (Cluster VORGANG, D1/D3). Bewusst DOM- und React-frei, damit die
// Regeln ohne Rendern prüfbar sind (Muster linkSlots.ts / mailCommandState.ts).

import type {
    VorgangPickerItem,
    VorgangPickerRang,
    VorgangPickerTodo,
} from "../../../services/vorgangService";

/**
 * Ein Zuordnungsziel: entweder der Vorgang selbst oder eines seiner Todos.
 *
 * T-34: Ein „neues To-do in Vorgang X" (Schlüssel `n:<vorgangId>`) ist noch
 * KEIN Ziel — es wird erst beim Zuordnen angelegt und dann zu `t:<id>`.
 */
export type PickerZiel = { vorgangId?: number; todoId?: number };

/**
 * Auswahl-Schlüssel. Ein `Set<number>` reichte NICHT: Vorgang 7 und Todo 7
 * wären derselbe Eintrag, und die Liste wechselt beim Tippen.
 */
export const vorgangKey = (id: number): string => `v:${id}`;
export const todoKey = (id: number): string => `t:${id}`;
/** T-34: „neues To-do in Vorgang <id>" — wird beim Zuordnen angelegt. */
export const neuesTodoKey = (vorgangId: number): string => `n:${vorgangId}`;
export const istNeuesTodoKey = (key: string): boolean => key.startsWith("n:");

/** Vorgang-Id aus einem `n:`-Schlüssel — alles andere liefert `null`. */
export function vorgangIdAusNeuKey(key: string): number | null {
    if (!istNeuesTodoKey(key)) return null;
    const id = Number(key.slice(2));
    return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Tauscht einen Schlüssel aus und behält den Elternvorgang als Wert (T-34:
 * aus `n:<v>` wird nach der Anlage `t:<id>`). Fehlt der alte Schlüssel, bleibt
 * die Auswahl unverändert.
 */
export function ersetzeKey(
    auswahl: ReadonlyMap<string, number>,
    alt: string,
    neu: string,
): Map<string, number> {
    const next = new Map(auswahl);
    const vorgangId = next.get(alt);
    if (vorgangId == null) return next;
    next.delete(alt);
    next.set(neu, vorgangId);
    return next;
}

/**
 * Schlüssel zurück in ein Zuordnungsziel — unbekannte Schlüssel fallen weg.
 * Auch `n:` (noch nicht angelegtes To-do) fällt weg: es darf nie als Ziel
 * durchrutschen.
 */
export function zieleAusKeys(keys: Iterable<string>): PickerZiel[] {
    const ziele: PickerZiel[] = [];
    for (const key of keys) {
        const id = Number(key.slice(2));
        if (!Number.isInteger(id) || id <= 0) continue;
        if (key.startsWith("v:")) ziele.push({ vorgangId: id });
        else if (key.startsWith("t:")) ziele.push({ todoId: id });
    }
    return ziele;
}

/**
 * Ziel für den Routenwechsel nach einer Mailzuordnung. Mehrere ausgewählte
 * To-dos desselben Vorgangs bleiben eindeutig; Ziele verschiedener Vorgänge
 * liefern bewusst `null`, damit die Oberfläche keinen davon willkürlich öffnet.
 */
export function eindeutigerVorgang(ids: Iterable<number>): number | null {
    const unique = new Set(
        Array.from(ids).filter((id) => Number.isInteger(id) && id > 0),
    );
    return unique.size === 1 ? Array.from(unique)[0] : null;
}

/** Überschrift einer Rangstufe (D1). */
export function rangTitel(rang: VorgangPickerRang): string {
    if (rang === 1) return "Vorgänge dieses Kontakts";
    if (rang === 2) return "Meine und Team-Vorgänge";
    return "Alle weiteren Vorgänge";
}

/** T-286: Überschrift der erledigten Vorgänge am Ende der Liste. */
export const ERLEDIGT_TITEL = "Erledigte Vorgänge";

/**
 * Gruppe einer Zeile: Rang 2 zerfällt in „Deine Vorgänge" (selbst beteiligt)
 * und „Team-Vorgänge" — der Server sortiert die eigenen innerhalb des Rangs
 * schon nach vorn (T-55), die Überschrift macht es sichtbar. Ohne `eigen`
 * (ältere Antwort) bleibt die gemeinsame Überschrift.
 */
export function gruppeVon(item: VorgangPickerItem): { schluessel: string; titel: string } {
    // T-286: Erledigte stehen gruppenübergreifend hinten (der Server sortiert
    // sie ans Ende) und bekommen deshalb eine eigene Überschrift.
    if (statusTon(item.vorgang.status) === "erledigt") {
        return { schluessel: "erledigt", titel: ERLEDIGT_TITEL };
    }
    if (item.rang === 2 && item.eigen !== undefined) {
        return item.eigen
            ? { schluessel: "2-eigen", titel: "Deine Vorgänge" }
            : { schluessel: "2-team", titel: "Team-Vorgänge" };
    }
    return { schluessel: String(item.rang), titel: rangTitel(item.rang) };
}

export type PickerZeile =
    | { kind: "gruppe"; rang: VorgangPickerRang; schluessel: string; titel: string }
    | { kind: "vorgang"; item: VorgangPickerItem };

/**
 * Fügt vor jedem Rangwechsel eine Gruppenüberschrift ein. Die Reihenfolge der
 * Server-Antwort bleibt unangetastet — der Server sortiert bereits nach
 * `offen-zuerst (T-286), rang, eigene-vor-Team (T-55), neueste-zuerst`.
 */
export function mitGruppenkoepfen(items: VorgangPickerItem[]): PickerZeile[] {
    const zeilen: PickerZeile[] = [];
    let letzte: string | null = null;
    for (const item of items) {
        const g = gruppeVon(item);
        if (g.schluessel !== letzte) {
            zeilen.push({ kind: "gruppe", rang: item.rang, schluessel: g.schluessel, titel: g.titel });
            letzte = g.schluessel;
        }
        zeilen.push({ kind: "vorgang", item });
    }
    return zeilen;
}

/** T-55: Überschrift über dem bereits verknüpften Vorgang. */
export const AKTUELL_TITEL = "Aktuell verknüpft";

export type PickerAuswahlZeile = PickerZeile | { kind: "aktuell"; titel: string };

/**
 * T-55: Wie {@link mitGruppenkoepfen}, aber der bereits verknüpfte Vorgang
 * bekommt eine eigene Überschrift „Aktuell verknüpft", wenn er vorn steht —
 * der Server zieht ihn mit `aktuell` nach oben. Der Rest wird normal
 * gruppiert; der Eintrag erscheint nicht doppelt.
 */
export function mitAktuellUndGruppen(
    items: VorgangPickerItem[],
    aktuelleVorgangId: number | null | undefined,
): PickerAuswahlZeile[] {
    if (aktuelleVorgangId == null || items[0]?.vorgang.id !== aktuelleVorgangId) {
        return mitGruppenkoepfen(items);
    }
    return [
        { kind: "aktuell", titel: AKTUELL_TITEL },
        { kind: "vorgang", item: items[0] },
        ...mitGruppenkoepfen(items.slice(1)),
    ];
}

/** T-55: Farbton des Status-Chips — erledigt grau, sonst „offen“. */
export function statusTon(status: string | null | undefined): "erledigt" | "offen" {
    return (status ?? "").trim().toLowerCase() === "erledigt" ? "erledigt" : "offen";
}

/** Nachladen (T-409: beim Scrollen) nur, solange der Server noch Zeilen hat. */
export function kannMehrLaden(geladen: number, total: number | null): boolean {
    if (total == null) return false;
    return geladen < total;
}

/**
 * Text für die leere Liste. Drei Fälle, die der Nutzer klar unterscheiden
 * können muss — „nichts gefunden" ist etwas anderes als „dieser Kontakt hat
 * noch keine Vorgänge".
 */
export function leerText(a: {
    mitSuche: boolean;
    mitKontakt: boolean;
    nurKontakt?: boolean;
    /** T-56: gewählter Bereich — bei Team/Eigene den Weg zurück nennen. */
    bereich?: "alle" | "team" | "persoenlich";
    /** T-348: Knopf „Persönliche Vorgänge“ aktiv — den Weg zurück nennen. */
    nurPersoenliche?: boolean;
}): string {
    if (a.nurPersoenliche) {
        return a.mitSuche
            ? "Keine Treffer unter deinen persönlichen Vorgängen – Knopf erneut drücken für alle."
            : "Keine offenen persönlichen Vorgänge – Knopf erneut drücken für alle.";
    }
    if (a.bereich === "persoenlich" || a.bereich === "team") {
        const was = a.bereich === "team" ? "Team-Vorgänge" : "eigenen Vorgänge";
        return a.mitSuche
            ? `Keine Treffer unter den ${was} – auf „Alle“ umschalten.`
            : `Keine ${was} – auf „Alle“ umschalten.`;
    }
    if (a.mitSuche) return "Keine Treffer.";
    if (a.mitKontakt && a.nurKontakt) return "Zu diesem Kontakt gibt es noch keine Vorgänge.";
    if (a.mitKontakt) return "Zu diesem Kontakt gibt es noch keine Vorgänge — und auch sonst keine.";
    return "Keine Vorgänge vorhanden.";
}

/**
 * Hinweis über der Liste, wenn es zwar Vorgänge gibt, aber keinen einzigen zu
 * DIESEM Kontakt (Stufe 1 fehlt). Ohne Kontakt und mit aktiver Suche: kein
 * Hinweis — dort ist die fehlende Stufe 1 keine Aussage.
 */
export function stufe1Hinweis(a: {
    mitSuche: boolean;
    mitKontakt: boolean;
    items: VorgangPickerItem[];
}): string | null {
    if (a.mitSuche || !a.mitKontakt || a.items.length === 0) return null;
    if (a.items.some((i) => i.rang === 1)) return null;
    return "Zu diesem Kontakt gibt es noch keine Vorgänge — unten stehen deine und alle weiteren.";
}

/**
 * Welche To-dos unter einer Vorgangszeile stehen (PO 30.08.2026).
 *
 * Zwei Quellen laufen hier zusammen: die TREFFER der Suche (liefert der Server
 * mit der Zeile, max. 5) und die beim AUFKLAPPEN nachgeladene Vollliste. Ist
 * aufgeklappt, gewinnt die Vollliste — sie enthält die Treffer ohnehin, und
 * beide zusammen zeigten Zeilen doppelt.
 */
export function sichtbareTodos(
    item: VorgangPickerItem,
    geladen: VorgangPickerTodo[] | undefined,
): VorgangPickerTodo[] {
    return geladen ?? item.todos;
}

/**
 * Lässt sich die Zeile aufklappen? Nur wenn der Vorgang To-dos hat.
 *
 * `todoAnzahl` zählt die AKTIVEN To-dos; die Suchtreffer allein genügen als
 * Kriterium nicht, denn sie sind auf fünf gedeckelt und ohne Suche leer.
 */
export function istAufklappbar(item: VorgangPickerItem): boolean {
    return (item.vorgang.todoAnzahl ?? 0) > 0;
}

/**
 * Sichtbarer Hinweis an einer Vorgangszeile mit To-dos (T-199). Vorher stand er
 * nur im `title` der gesperrten Checkbox — auf dem Phone (ohne Hover) erschien
 * er nie, und ein Tipp auf die Zeile tat scheinbar nichts.
 */
export const NUR_TODO_HINWEIS = "In To-dos gegliedert – bitte ein To-do wählen";

/**
 * Beschriftung des Aufklapp-Knopfs. Nennt die Zahl, damit man vor dem Klick
 * weiß, ob sich einer lohnt.
 */
export function aufklappLabel(item: VorgangPickerItem, offen: boolean): string {
    const n = item.vorgang.todoAnzahl ?? 0;
    const was = n === 1 ? "1 To-do" : `${n} To-dos`;
    return offen ? `${was} einklappen` : `${was} anzeigen`;
}

/**
 * T-286: Datum kurz — im laufenden Jahr ohne Jahreszahl („08.09."), sonst mit
 * („08.09.2025"). Erwartet `YYYY-MM-DD` (bzw. ISO); Unlesbares bleibt, wie es ist.
 */
export function kurzDatum(wert: string, heute: Date = new Date()): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(wert.trim());
    if (!m) return wert;
    const [, j, mo, t] = m;
    return Number(j) === heute.getFullYear() ? `${t}.${mo}.` : `${t}.${mo}.${j}`;
}

/**
 * Meta-Zeile einer Vorgangszeile: Nummer · Kunde · Status, und bei Vorgängen
 * aus einem früheren Jahr das Anlagejahr (T-286: „Jahr mitanzeigen, wenn nicht
 * 2026") — sonst sieht ein alter Vorgang aus wie ein frischer.
 */
export function metaZeile(item: VorgangPickerItem, heute: Date = new Date()): string {
    const jahr = Number(/^(\d{4})-/.exec(item.vorgang.createdAt ?? "")?.[1]);
    const anlage = Number.isInteger(jahr) && jahr > 0 && jahr !== heute.getFullYear() ? `angelegt ${jahr}` : null;
    return [item.vorgang.vorgangNbr, item.vorgang.kundeName, item.vorgang.status, anlage]
        .filter(Boolean)
        .join(" · ");
}

/**
 * TWV/Frist einer Vorgangszeile. An erledigten Vorgängen nichts (T-290: seit
 * T-218 trägt ein erledigter Vorgang keine Termine mehr; ein später von Hand
 * nachgetragener soll im Picker nicht wieder verwirren).
 */
export function terminZeile(item: VorgangPickerItem, heute: Date = new Date()): string {
    if (statusTon(item.vorgang.status) === "erledigt") return "";
    return [
        item.vorgang.twv ? `TWV ${kurzDatum(item.vorgang.twv, heute)}` : null,
        item.vorgang.frist ? `Frist ${kurzDatum(item.vorgang.frist, heute)}` : null,
    ]
        .filter(Boolean)
        .join(" · ");
}
